// The one envelope reader. Every backend response is
// `{success, code, message, request_id, data}`, and the wire tells the truth
// twice: an error ships its real HTTP status *and* `success: false` with the
// seven-digit code (backend/errors/README.md → "The wire"). Statuses are a
// convenience for proxies and monitors, the body is the contract — so `unwrap`
// guards `success` whatever the status was, and `client.ts`'s response
// interceptor is what turns an HTTP failure into an ApiError. No api function
// may skip either.
import { ApiError } from "./errors";

import type { ApiErrorDetail } from "./errors";
import type { AxiosResponse } from "axios";

export interface Envelope<T> {
  success: boolean;
  code: number | null;
  message: string;
  request_id: string;
  data: T | null;
  details?: ApiErrorDetail[];
}

/**
 * Unwrap `data`, or throw the same ApiError an HTTP failure would have thrown.
 *
 * It takes the whole response, not the body, because the body cannot say what
 * status it arrived with — and a caller that cannot tell a 409 from a 500
 * cannot decide whether retrying is worth anything.
 */
export function unwrap<T>(res: AxiosResponse<Envelope<T>>): T {
  const body = res?.data;
  if (!body?.success) {
    throw new ApiError(
      body?.message || "Something went wrong",
      res?.status ?? 0,
      body?.code ?? null,
      body?.request_id ?? null,
      body?.details,
    );
  }
  return body.data as T;
}
